export const DEFAULT_TIME_ZONE = "America/Edmonton";

const DATE_ONLY_RX = /^\d{4}-\d{2}-\d{2}$/;

const FALLBACK_TIME_ZONES = [
  "America/Edmonton",
  "America/Vancouver",
  "America/Winnipeg",
  "America/Toronto",
  "America/Halifax",
  "America/St_Johns",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const toTrimmedString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export const isDateOnlyValue = (value) => DATE_ONLY_RX.test(toTrimmedString(value));

export const isValidTimeZone = (value) => {
  const candidate = toTrimmedString(value);
  if (!candidate) return false;

  try {
    Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(new Date());
    return true;
  } catch (_error) {
    return false;
  }
};

export const normalizeTimeZone = (value, fallback = DEFAULT_TIME_ZONE) => {
  const candidate = toTrimmedString(value);
  return isValidTimeZone(candidate) ? candidate : fallback;
};

export const getInstanceTimeZone = (instance = {}) =>
  normalizeTimeZone(
    instance?.timezone ||
      instance?.timeZone ||
      instance?.Timezone ||
      instance?.TimeZone
  );

export const getBrowserTimeZone = () => {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch (_error) {
    return DEFAULT_TIME_ZONE;
  }
};

export const listSupportedTimeZones = () => {
  const values = typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : FALLBACK_TIME_ZONES;

  return Array.from(new Set(values.filter(isValidTimeZone)));
};

export const formatDateTimeInTimeZone = (
  value,
  {
    timeZone = DEFAULT_TIME_ZONE,
    locale = "en-US",
    includeTimeZoneName = false,
    options = {},
  } = {}
) => {
  if (!value) return "";
  if (isDateOnlyValue(value)) return toTrimmedString(value);

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return toTrimmedString(value);

  return new Intl.DateTimeFormat(locale, {
    timeZone: normalizeTimeZone(timeZone),
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(includeTimeZoneName ? { timeZoneName: "short" } : {}),
    ...options,
  }).format(parsed);
};

export const formatInstanceDateTime = (value, instance, options = {}) =>
  formatDateTimeInTimeZone(value, {
    timeZone: getInstanceTimeZone(instance),
    ...options,
  });

export const formatDateOnlyValue = (value, locale = "en-US") => {
  if (!value) return "";

  const raw = toTrimmedString(value);
  const onlyDate = raw.includes("T") ? raw.split("T")[0] : raw;
  if (!DATE_ONLY_RX.test(onlyDate)) return raw;

  const parsed = new Date(`${onlyDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return raw;

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
};
